import {
  CHANNEL,
  PAGE_NAME_PC,
  PAGE_NAME_SP,
  JOB_RANKING,
  JOB_CARD_CLICK,
} from '../../../constants/seo/jobRankingConstants';
import { adobeAnalyticsScriptsSelector } from '../../../redux/features/analytics';
import { tl, tlReadyExecute } from '../../../utils/adobeAnalyticsUtil';
import { dataLayer } from '../../../utils/googleTagManagerUtil';
import {
  AnalyticsAppThunk,
  NeededStateForAnalytics,
} from '../../../types/analytics';

/**
 * 求人カセットClick計測
 * @param url 求人詳細URL
 * @returns sid付き求人詳細URL
 */
export const createJobCardLink = (url: string | undefined) => {
  return `${url}&sid=${JOB_RANKING}${JOB_CARD_CLICK}`;
};

/**
 * 求人カセット気になるボタンClick計測
 * @param jid 求人ID
 * @returns 気になるボタンClick計測
 */
export const measureClickJobCardFavorite =
  <S extends NeededStateForAnalytics>(jid: string): AnalyticsAppThunk<S> =>
  (dispatch, getState) => {
    // AA計測
    const isAdobeScript = adobeAnalyticsScriptsSelector(getState());
    tlReadyExecute(isAdobeScript, dispatch, 'kininaruClick_PopularRanking', {
      linkTrackVars: 'events,prop23,eVar23',
      linkTrackEvents: 'event120',
      products: `;${jid}`,
      prop23: 'kininaru_Click',
      prop32: jid,
      eVar23: 'kininaru_Click',
      events: 'event120',
    });
    // GA計測
    dataLayer({
      JobId: jid,
      event: 'bookmark_click',
    });
  };

/**
 * レコメンドインプレッション計測
 * @param recommendType レコメンドタイプ
 */
export const measureImpressionRecommend = (recommendType: string) => {
  tl('recommendImpressionTrack', {
    linkTrackVars: 'prop71',
    prop71: recommendType,
  });
};

/**
 * レコメンドカードの求人詳細Click計測
 * @param recommendType レコメンドタイプ
 * @param effectName effectName
 * @param isSP SP/PC判定
 * @returns レコメンド求人詳細Click計測
 */
export const measureClickRecommendCard =
  <S extends NeededStateForAnalytics>(
    recommendType: string,
    effectName: string,
    isSP: boolean
  ): AnalyticsAppThunk<S> =>
  (dispatch, getState) => {
    // AA計測
    const isAdobeScript = adobeAnalyticsScriptsSelector(getState());
    tlReadyExecute(isAdobeScript, dispatch, 'siteRecommendTracking', {
      linkTrackVars: 'eVar103,eVar105,eVar187',
      eVar103: recommendType,
      eVar105: CHANNEL,
      eVar187: isSP ? PAGE_NAME_SP : PAGE_NAME_PC,
    });
    // GA計測
    dataLayer({
      siteRecommendLogic: recommendType,
      siteRecommendTrackParam: effectName,
      event: 'siteRecommendTracking',
    });
  };

/**
 * レコメンドカードの気になるClick計測
 * @param jid 求人ID
 * @returns レコメンドカードの気になる押下時の計測
 */
export const measureClickRecommendCardBookmark =
  <S extends NeededStateForAnalytics>(jid: string): AnalyticsAppThunk<S> =>
  (dispatch, getState) => {
    // AA計測
    const isAdobeScript = adobeAnalyticsScriptsSelector(getState());
    tlReadyExecute(
      isAdobeScript,
      dispatch,
      'kininaruClick_PopularRanking_rec',
      {
        linkTrackVars: 'events,prop23,eVar23',
        linkTrackEvents: 'event120',
        products: `;${jid}`,
        prop23: 'kininaru_Click',
        prop32: jid,
        eVar23: 'kininaru_Click',
        events: 'event120',
      }
    );
    // GA計測
    dataLayer({
      JobId: jid,
      event: 'bookmark_click',
    });
  };

/**
 * 同フラグのコンテンツリンク 求人一覧リンクClick計測
 * @returns 求人一覧リンクClick計測
 */
export const measureClickJobSearchListLink = () => () => {
  tl('Jobranking_jobsearchlist_click', {});
};

/**
 * 同フラグのコンテンツリンク 求人の傾向リンクClick計測
 * @returns 求人の傾向リンクClick計測
 */
export const measureClickJobTrendLink = () => () => {
  tl('Jobranking_jobtrend_click', {});
};

/**
 * Dispatches an analytics event on job ranking page when the occupation link is clicked.
 * @returns A thunk that dispatches the analytics event.
 */
export const measureClickOccupationLink = () => () => {
  tl('Jobranking_Occupation_click', {});
};
